import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Heart, ArrowLeft, Plus, Trash2, BadgeCheck, Clock, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [emails, setEmails] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error("Your new password must be at least 8 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("The new passwords don't match."); return; }
    setSavingPw(true);
    try { await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next }); toast.success("Password updated"); setPw({ current: "", next: "", confirm: "" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const load = () => api.get("/auth/emails").then(({ data }) => setEmails(data)).catch(() => setEmails({ primary: {}, extra: [] }));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newEmail) return;
    setBusy(true);
    try { await api.post("/auth/emails", { email: newEmail }); toast.success(`Verification email sent to ${newEmail}`); setNewEmail(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (email) => {
    try { await api.post("/auth/emails/delete", { email }); load(); toast.success("Email removed"); }
    catch { toast.error("Could not remove that email."); }
  };
  const resendPrimary = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent."); }
    catch { toast.error("Could not resend."); }
  };

  const Row = ({ email, verified, primary, onResend }) => (
    <div className="flex items-center justify-between gap-3 border border-[#E8E6E1] rounded-lg px-4 py-3 bg-white" data-testid={`email-row-${email}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-stone-800 truncate">{email} {primary && <span className="text-[11px] text-stone-400">(primary)</span>}</p>
        {verified
          ? <span className="inline-flex items-center gap-1 text-xs text-[#5f6b59] mt-0.5"><BadgeCheck size={13} /> Verified</span>
          : <span className="inline-flex items-center gap-1 text-xs text-[#b08968] mt-0.5"><Clock size={13} /> Pending verification</span>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!verified && onResend && <button onClick={onResend} data-testid={`resend-${email}`} className="text-xs text-[#8C9A86] hover:underline">Resend</button>}
        {!primary && <button onClick={() => remove(email)} data-testid={`remove-${email}`} className="p-1.5 text-stone-400 hover:text-[#C67A7A]"><Trash2 size={15} /></button>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "#F5F4F0" }} data-testid="account-page">
      <header className="border-b border-[#E8E6E1] bg-white/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2 text-stone-600 hover:text-stone-900"><ArrowLeft size={16} /> <span className="font-serif text-xl">Evermore</span></Link>
          <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#C67A7A]"><LogOut size={15} /> Sign out</button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <p className="overline">Your account</p>
        <h1 className="font-serif text-4xl tracking-tight">{user?.name}</h1>
        <p className="text-stone-500 mt-3 max-w-xl">Add all the email addresses friends and family might use to invite you. Each must be verified so we always match invitations and photos to the right person.</p>

        {!emails ? <p className="text-stone-400 mt-8">Loading…</p> : (
          <div className="mt-8 space-y-3">
            <Row email={emails.primary.email} verified={emails.primary.verified} primary onResend={!emails.primary.verified ? resendPrimary : null} />
            {(emails.extra || []).map((e) => <Row key={e.email} email={e.email} verified={e.verified} />)}

            <div className="flex gap-2 pt-2">
              <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} type="email" placeholder="add another email…" data-testid="add-email-input"
                className="flex-1 rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
              <button onClick={add} disabled={busy} data-testid="add-email-btn"
                className="flex items-center gap-1.5 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-50"><Plus size={15} /> Add</button>
            </div>
          </div>
        )}

        <div className="mt-12 border-t border-[#E8E6E1] pt-10" data-testid="password-section">
          <p className="overline">Security</p>
          <h2 className="font-serif text-3xl tracking-tight">Change your password</h2>
          <p className="text-stone-500 mt-2 text-sm max-w-xl">Use at least 8 characters. You'll stay signed in on this device.</p>
          <div className="mt-6 space-y-3 max-w-sm">
            <PwField label="Current password" value={pw.current} onChange={(v) => setPw({ ...pw, current: v })} testid="cur-password-input" />
            <PwField label="New password" value={pw.next} onChange={(v) => setPw({ ...pw, next: v })} testid="new-password-input" />
            <PwField label="Confirm new password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} testid="confirm-password-input" />
            <button onClick={changePassword} disabled={savingPw || !pw.current || !pw.next} data-testid="change-password-btn"
              className="rounded-full bg-[#2C2A28] text-white px-6 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-50">
              {savingPw ? "Updating…" : "Update password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type="password" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
